/**
 * Feature flags — single source of truth for the frontend.
 *
 * Mirrors the backend `packages/go-features` Flag enum. Phase 1 ships with
 * every flag OFF. Flips happen at deploy time via Vite env vars (prefixed
 * VITE_FEATURE_) so the staging and production builds can flip simultaneously
 * by promoting the same env config — see extras/payment/payment-architecture-implementation-plan.md §0.3.
 *
 * Backend and frontend MUST agree on a flag's state at any given moment.
 * Promote env changes to both deploys together.
 */

export type FeatureFlag =
  // Phase 2 — wallet, KYC, vendor payments
  | 'ATTENDEE_WALLET'
  | 'KYC_TIER_1'
  | 'KYC_TIER_2'
  | 'VENDOR_PAYMENTS'
  | 'VENDOR_ORDER_BOARD'
  | 'VENDOR_EARNINGS_DASHBOARD'
  | 'VENDOR_WITHDRAWALS'
  | 'AUTOMATED_KYB'
  | 'AUTOMATED_KYC_PROVIDER'
  // Phase 3 — advanced rails
  | 'KYC_TIER_3'
  | 'TRANSACTION_MONITORING'
  | 'AUTOMATED_PAYOUT_SCHEDULING'
  | 'INTERNATIONAL_RAILS'
  | 'FLOAT_INCOME'
  | 'REVENUE_ADVANCE'

function envFlag(name: FeatureFlag): boolean {
  const raw = (import.meta.env[`VITE_FEATURE_${name}`] as string | undefined) ?? ''
  return ['1', 'true', 'TRUE', 'yes', 'on'].includes(raw.trim())
}

export const FEATURES: Record<FeatureFlag, boolean> = {
  ATTENDEE_WALLET:             envFlag('ATTENDEE_WALLET'),
  KYC_TIER_1:                  envFlag('KYC_TIER_1'),
  KYC_TIER_2:                  envFlag('KYC_TIER_2'),
  VENDOR_PAYMENTS:             envFlag('VENDOR_PAYMENTS'),
  VENDOR_ORDER_BOARD:          envFlag('VENDOR_ORDER_BOARD'),
  VENDOR_EARNINGS_DASHBOARD:   envFlag('VENDOR_EARNINGS_DASHBOARD'),
  VENDOR_WITHDRAWALS:          envFlag('VENDOR_WITHDRAWALS'),
  AUTOMATED_KYB:               envFlag('AUTOMATED_KYB'),
  AUTOMATED_KYC_PROVIDER:      envFlag('AUTOMATED_KYC_PROVIDER'),
  KYC_TIER_3:                  envFlag('KYC_TIER_3'),
  TRANSACTION_MONITORING:      envFlag('TRANSACTION_MONITORING'),
  AUTOMATED_PAYOUT_SCHEDULING: envFlag('AUTOMATED_PAYOUT_SCHEDULING'),
  INTERNATIONAL_RAILS:         envFlag('INTERNATIONAL_RAILS'),
  FLOAT_INCOME:                envFlag('FLOAT_INCOME'),
  REVENUE_ADVANCE:             envFlag('REVENUE_ADVANCE'),
}

export function isEnabled(flag: FeatureFlag): boolean {
  return FEATURES[flag]
}
